// apps/web/src/routes/PublicProfile.tsx
//
// /@:handle  — public profile (phase-1.md §6, /people/:handle).
// Unauthenticated; shows the public subset of a user row (handle,
// display name, first name, country, city, profile pic, member-since).
// Renders 404 for unknown / soft-deleted / malformed handles with
// the same response (no enumeration leak).

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, ApiError } from '../lib/apiClient.js';
import type { PublicProfile as PublicProfileShape } from '../lib/api.js';
import { SkeletonBlock } from '../components/ui/Skeleton.js';
import { Alert } from '../components/ui/Alert.js';

interface State {
  status: 'loading' | 'notfound' | 'ok' | 'error';
  profile?: PublicProfileShape;
  requestId?: string | null;
  error?: string;
}

export function PublicProfile(): JSX.Element {
  const params = useParams<{ handle: string }>();
  const handle = params.handle ?? '';
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    if (!handle) {
      setState({ status: 'notfound' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    apiGet<{ profile: PublicProfileShape }>(`/people/${encodeURIComponent(handle)}`)
      .then((res) => {
        if (cancelled) return;
        setState({ status: 'ok', profile: res.data.profile, requestId: res.requestId });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const apiErr = err as ApiError;
        if (apiErr?.status === 404) {
          setState({ status: 'notfound' });
        } else {
          setState({
            status: 'error',
            error: apiErr?.message ?? 'Could not load profile.',
            requestId: apiErr?.requestId ?? null,
          });
        }
      });
    return () => { cancelled = true; };
  }, [handle]);

  if (state.status === 'loading') {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12">
        <SkeletonBlock className="h-8 w-48" />
        <SkeletonBlock className="mt-4 h-4 w-1/2" />
        <SkeletonBlock className="mt-6 h-32 w-32 rounded-full" />
      </section>
    );
  }

  if (state.status === 'notfound') {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12 text-center">
        <h1 className="text-2xl font-bold">@{handle} not found</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
          This profile doesn&apos;t exist or is no longer available.
        </p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12">
        <Alert variant="error" title="Could not load profile" requestId={state.requestId ?? null}>
          {state.error}
        </Alert>
      </section>
    );
  }

  const p = state.profile!;
  const memberSince = new Date(p.memberSince).toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  const ig = p.instagram;

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <div className="flex items-start gap-6">
        {p.profilePic ? (
          <img
            src={p.profilePic}
            alt=""
            className="h-32 w-32 rounded-full border border-gray-200 object-cover dark:border-gray-800"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-32 w-32 items-center justify-center rounded-full bg-brand-100 text-3xl font-bold text-brand-600 dark:bg-brand-900 dark:text-brand-300"
          >
            {p.firstName.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{p.displayName}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">@{p.handle}</p>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            {[p.firstName, p.lastName].filter(Boolean).join(' ')}
            {p.city ? ` · ${p.city}, ` : ' · '}
            {p.country}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Member since {memberSince}
          </p>
        </div>
      </div>

      {ig && (
        <div className="instagram-public" data-testid="public-instagram">
          <header className="instagram-public-header">
            {ig.profilePictureUrl && <img src={ig.profilePictureUrl} alt="" className="instagram-public-avatar" />}
            <div>
              <a href={ig.profileUrl} target="_blank" rel="noopener noreferrer" className="instagram-public-username">@{ig.username}</a>
              <p className="muted">
                Instagram · {ig.accountType}
                {ig.followerCount != null ? ` · ${ig.followerCount.toLocaleString()} followers` : ''}
                {ig.mediaCount != null ? ` · ${ig.mediaCount} posts` : ''}
              </p>
            </div>
          </header>
          {ig.recentMedia.length > 0 ? (
            <div className="instagram-public-grid">
              {ig.recentMedia.map((m) => (
                <a key={m.id} href={m.permalink ?? ig.profileUrl} target="_blank" rel="noopener noreferrer" className="instagram-public-tile">
                  {(m.thumbnailUrl || m.mediaUrl) ? <img src={(m.thumbnailUrl ?? m.mediaUrl)!} alt="" loading="lazy" /> : <div className="instagram-public-placeholder">{m.type ?? 'media'}</div>}
                  <div className="instagram-public-meta">
                    <span>♥ {m.likeCount ?? '—'}</span>
                    <span>💬 {m.commentsCount ?? '—'}</span>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className="muted">No media cached yet — the next daily sync will pull the most recent posts.</p>
          )}
          {ig.lastSyncedAt && (
            <p className="muted instagram-public-foot">Last synced {new Date(ig.lastSyncedAt).toLocaleString()}</p>
          )}
        </div>
      )}
    </section>
  );
}
